import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Link } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { getMyOverview } from '../../../requests/customerPortal';
import {
  btnPrimary,
  btnSecondary,
  Card,
  PageBody,
  PageHeader,
  Pill,
} from '../../Admin/ui';
import {
  BalanceBar,
  fmtDate,
  fmtMoney,
  fmtNum,
  LoadFailed,
  Stat,
  retryUnless4xx,
} from '../shared';

const AdminHome: React.FC = () => {
  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'overview'],
    queryFn: getMyOverview,
  });

  if (isLoading) return <LoadingSpinner />;
  if (error || !data) return <LoadFailed error={error} />;

  return (
    <>
      <PageHeader
        title={data.account.accountName ?? 'Your organisation'}
        meta="Your programs, people and requests in one place."
      />
      <PageBody>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat
            label="Upcoming private events"
            value={fmtNum(data.upcomingPrivateEvents)}
            caption={<Link to={PATH.adminEvents}>See the schedule →</Link>}
          />
          <Stat
            label="Registrations this year"
            value={fmtNum(data.registrationsThisYear)}
            caption={
              <Link to={PATH.adminRegistrations}>Review registrations →</Link>
            }
          />
          <Stat
            label="Evaluations received"
            value={fmtNum(data.evaluations)}
            caption={
              <Link to={PATH.adminEvaluations}>Review evaluations →</Link>
            }
          />
          <Stat
            label="Open tickets"
            value={fmtNum(data.openTickets)}
            caption={<Link to={PATH.adminTickets}>Tickets →</Link>}
          />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Card
              title="Your programs"
              right={
                <Link
                  to={PATH.adminPrograms}
                  className="text-xs font-medium text-blue-primary"
                >
                  All programs
                </Link>
              }
            >
              {data.programs.length === 0 ? (
                <div className="px-5 py-8 text-sm text-gray-bold">
                  No active program on this account.
                </div>
              ) : (
                <div className="grid gap-px bg-white-three sm:grid-cols-2">
                  {data.programs.map((p) => (
                    <Link
                      key={p.id}
                      to={`${PATH.adminPrograms}?id=${p.id}`}
                      className="block bg-white px-5 py-4 transition-colors hover:bg-grey-50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-semibold text-gray-five">
                          {p.name}
                        </div>
                        <Pill value={p.status} />
                      </div>
                      <div className="mt-1 text-xs text-gray-bold">
                        {fmtDate(p.startDate)} – {fmtDate(p.endDate)} ·{' '}
                        {fmtNum(p.memberCount)} members
                      </div>
                      <div className="mt-3 flex items-baseline justify-between text-sm">
                        <span className="text-gray-three">Remaining</span>
                        <span className="font-semibold text-gray-five">
                          {fmtMoney(p.remaining)}
                          <span className="ml-1 text-xs font-normal text-gray-bold">
                            of {fmtMoney(p.invested)}
                          </span>
                        </span>
                      </div>
                      <BalanceBar used={p.used} invested={p.invested} />
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <div className="flex flex-col gap-6">
            <Card title="Would you like to schedule a course?">
              <div className="px-5 py-4 text-sm text-gray-three">
                Submit a private workshop request in under two minutes. Our team
                checks instructor availability and comes back to you to finalise
                the details.
                <div className="mt-4">
                  <Link to={PATH.adminRequestCourse} className={btnPrimary}>
                    Request a private course
                  </Link>
                </div>
              </div>
            </Card>
            <Card title="Quick connect">
              <div className="px-5 py-4 text-sm text-gray-three">
                Need help or a change? Open a ticket and you will hear back
                within one business day, usually faster.
                <div className="mt-4">
                  <Link
                    to={`${PATH.adminTickets}?new=1`}
                    className={btnSecondary}
                  >
                    Submit a ticket
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );
};

export default AdminHome;
